// hooks/useExtendedCarDataListener.js
import { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "@/config/firebaseConfig";

const FIRST_SNAPSHOT_TIMEOUT_MS = 5000;

/** timestamp -> millis (robust gegen verschiedene Formen) */
function toMillis(v) {
  if (!v && v !== 0) return -Infinity;
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const t = Date.parse(v);
    return isNaN(t) ? -Infinity : t;
  }
  if (typeof v === "object") {
    if (typeof v.seconds === "number") return v.seconds * 1000;
    if (typeof v._seconds === "number") return v._seconds * 1000;
    if (typeof v.toDate === "function") {
      try {
        return v.toDate().getTime();
      } catch {}
    }
  }
  return -Infinity;
}

export default function useExtendedCarDataListener(
  statusFilter = "extended",
  opts = {}
) {
  const { enabled = true, initialData = [] } = opts;

  const [carData, setCarData] = useState(initialData);
  const [loading, setLoading] = useState(!!enabled);

  const unsubRef = useRef(null);
  const debounceRef = useRef(null);
  const mergedMapRef = useRef(Object.create(null));
  const firstSnapshotRef = useRef(false);
  const firstSnapshotTimerRef = useRef(null);

  const cleanup = () => {
    try {
      unsubRef.current?.();
    } catch {}
    unsubRef.current = null;
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (firstSnapshotTimerRef.current) {
      clearTimeout(firstSnapshotTimerRef.current);
      firstSnapshotTimerRef.current = null;
    }
  };

  const flush = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const arr = Object.values(mergedMapRef.current);
      // Sortiere nach lastUpdated (fallback -Infinity, damit undefiniert nach hinten fällt)
      arr.sort((a, b) => toMillis(b?.lastUpdated) - toMillis(a?.lastUpdated));
      setCarData(arr);
      setLoading(false);
    }, 120);
  };

  useEffect(() => {
    if (!enabled) {
      cleanup();
      setLoading(false);
      return;
    }

    setLoading(true);
    mergedMapRef.current = Object.create(null);
    firstSnapshotRef.current = false;

    if (firstSnapshotTimerRef.current) {
      clearTimeout(firstSnapshotTimerRef.current);
      firstSnapshotTimerRef.current = null;
    }
    firstSnapshotTimerRef.current = setTimeout(() => {
      if (!firstSnapshotRef.current) {
        setCarData([]);
        setLoading(false);
      }
    }, FIRST_SNAPSHOT_TIMEOUT_MS);

    const col = collection(db, "USERTOFIN");
    const qRef = query(col, where("status", "==", statusFilter));

    unsubRef.current = onSnapshot(
      qRef,
      (snap) => {
        firstSnapshotRef.current = true;
        if (firstSnapshotTimerRef.current) {
          clearTimeout(firstSnapshotTimerRef.current);
          firstSnapshotTimerRef.current = null;
        }

        snap.docChanges().forEach((change) => {
          const id = change.doc.id;

          if (change.type === "removed") {
            delete mergedMapRef.current[id];
            return;
          }

          const data = change.doc.data();
          // Keine Users-Anreicherung mehr – direkt übernehmen
          mergedMapRef.current[id] = { emailFinKey: id, ...data };
        });

        flush();
      },
      (err) => {
        firstSnapshotRef.current = true;
        if (firstSnapshotTimerRef.current) {
          clearTimeout(firstSnapshotTimerRef.current);
          firstSnapshotTimerRef.current = null;
        }
        console.warn(
          "[useExtendedCarDataListener] listener error:",
          err?.code || err?.message
        );
        setLoading(false);
      }
    );

    return cleanup;
  }, [enabled, statusFilter]);

  return { carData, loading };
}
